import type { ModalInitialStateType } from 'src/helper/shared-types';
import {
  ONBOARDING_STEPS,
  type OnboardingPageType,
  type OnboardingSteps,
  type OnboardingStepsType,
} from 'src/redux/vendor-portal/onboarding/types';

export type StepperType = {
  key: OnboardingSteps;
  label: string;
};

export const ONBOARDING_BUTTON_LABELS = {
  BACK: 'Back',
  INITIAL_ACTION: 'Get Started',
  SAVE_CONTINUE: 'Save & Continue',
  SAVE_PASSWORD_CONTINUE: 'Set Password & Continue',
  COMPLETE_ONBOARDING: 'Complete Onboarding',
} as const;

export const ONBOARDING_DESCRIPTIONS: Omit<OnboardingStepsType<string>, 'UNDER_REVIEW'> = {
  BASIC_INFO: 'Please confirm or update your company’s contact and address details.',
  SET_PASSWORD:
    'Create a secure password for your Foresight account. You’ll use this along with your email to log in.',
  SERVICE_AREA: 'Select your service area. You’ll receive work orders from within this region.',
  SERVICES_OFFERED:
    'Choose the services your company provides. We’ll use this to match you with the most relevant work orders.',
};

export const ONBOARDING_FOOTER: string = 'Copyright © 2025 Foresight. All rights reserved.';

export const SYNCED_FIELD_COPY = {
  NOTICE:
    'Some of your details are managed by your property management system and can’t be edited here.',
  TOOLTIP: 'Synced from your property management system',
} as const;

export const ONBOARDING_PARAGRAPHS: Pick<
  OnboardingPageType<string[]>,
  'INITIAL' | 'EXPIRED' | 'UNDER_REVIEW' | 'SERVICE_AREA'
> = {
  EXPIRED: [
    'This link is no longer valid because it has expired after 7 days.',
    'To get a new invitation, please contact',
    'directly.',
  ],
  INITIAL: [
    'Foresight helps you stay organized and in control. With our easy-to-use platform, you can manage your team, track performance, and maintain the info property managers care about — all in one place.',
    'We’ll guide you through a few quick steps to set up your company profile and start receiving work orders.',
    'Takes ~5 minutes',
  ],
  UNDER_REVIEW: [
    'Your information has been submitted and is now awaiting coordinator approval. We’ll email you as soon as your account is live.',
    'If you need to update any information, you can do that after approval.',
  ],
  SERVICE_AREA: ['You can click on the map to add more ZIP codes to your service area.'],
};

export const ONBOARDING_STEPPER_LABELS: OnboardingStepsType<string> = {
  BASIC_INFO: 'Basic Info',
  SET_PASSWORD: 'Set Password',
  SERVICE_AREA: 'Service Area',
  SERVICES_OFFERED: 'Services Offered',
  UNDER_REVIEW: 'Under Review',
};

export const ONBOARDING_TITLES: OnboardingPageType<string> = {
  BASIC_INFO: 'Basic Information',
  EXPIRED: 'This Invitation Link Has Expired',
  INITIAL: 'Welcome to Foresight',
  SET_PASSWORD: 'Set Your Password',
  SERVICE_AREA: 'Service Area',
  SERVICES_OFFERED: 'Services Offered',
  UNDER_REVIEW: 'Your Account is Under Review',
};

export const STEP_MAPPING: StepperType[] = [
  {
    key: ONBOARDING_STEPS.SET_PASSWORD,
    label: ONBOARDING_STEPPER_LABELS.SET_PASSWORD,
  },
  {
    key: ONBOARDING_STEPS.BASIC_INFO,
    label: ONBOARDING_STEPPER_LABELS.BASIC_INFO,
  },
  {
    key: ONBOARDING_STEPS.SERVICES_OFFERED,
    label: ONBOARDING_STEPPER_LABELS.SERVICES_OFFERED,
  },
  {
    key: ONBOARDING_STEPS.SERVICE_AREA,
    label: ONBOARDING_STEPPER_LABELS.SERVICE_AREA,
  },
];

const IS_REQUIRED: string = 'is required.';

export const VALIDATION_MESSAGES = {
  INVALID: {
    PHONE: 'Phone number is invalid.',
    ZIP: 'ZIP code is invalid.',
  },
  REQUIRED: {
    CITY: `City ${IS_REQUIRED}`,
    FIRST_NAME: `First name ${IS_REQUIRED}`,
    LAST_NAME: `Last name ${IS_REQUIRED}`,
    PHONE: `Phone number ${IS_REQUIRED}`,
    STATE: `State ${IS_REQUIRED}`,
    STREET: `Street address ${IS_REQUIRED}`,
    ZIP: `Zip code ${IS_REQUIRED}`,
  },
};

export const EXPIRED_PAGE_ROUTE: string = '/onboarding/expired?company_name=';

export const SIGNED_URL_PARAMS = {
  COMPANY: 'company_name',
  DATA: 'data',
  EXPIRES: 'expires',
  SIGNATURE: 'signature',
  TOKEN: 'token',
};
export const ONBOARDING_LOOKUP: string = 'vendor-onboarding';

export const SERVICES_OFFERED_CHECK_STATE = {
  CHECKED: 'checked',
  INDETERMINATE: 'indeterminate',
};

export const SERVICE_AREA_CONFIRM_MODAL = {
  hideIcon: true,
  modalTitle: 'Remove All Selected ZIP Codes',
  successButtonLabel: 'Remove All ZIP Codes',
  successButtonColor: 'ls-button ls-button-danger has-danger btn-danger',
  cancelText: 'Cancel',
  cancelButtonColor: 'ls-button ls-button-secondary has-secondary',
};

export const SERVICE_AREA_SHOW_ZIPS_MODAL = {
  hideIcon: true,
  successButtonLabel: 'Done',
  successButtonColor: 'ls-button ls-button-primary has-primary has-solid',
  customModalClassName: 'service-area-modal',
  hideCloseButton: true,
};

export const SERVICE_AREA_MILES_OPTIONS = [5, 10, 15, 20, 25, 30, 40, 50];
export const SERVICE_AREA_ZOOM_VALUE = {
  5: 9,
  10: 8.3,
  15: 8.1,
  20: 7.8,
  25: 7.5,
  30: 7.2,
  40: 7,
  50: 6.6,
};
export const SERVICE_AREA_FORM_ID: string = 'service-area-form';

export const SERVICE_AREA_CONFIRM_MODAL_TYPE: keyof ModalInitialStateType = 'commonDeleteModal';

export const SERVICE_AREA_MAP_LAYER = {
  zipHighlighted: 'zips-highlighted',
  zipUnselected: 'zips-unselected',
  zipHover: 'zips-hover',
  boundingCircle: 'bounding-circle-layer',
};

export const SERVICE_AREA_MAP_SOURCE = {
  zip: 'zips',
  boundingCircle: 'bounding-circle',
  mapId: 'map',
  pointStyle: {
    'fill-color': 'rgba(19, 105, 233, 0.40)',
    'fill-opacity': 1,
  },
  tooltipStyle: 'width: max-content;max-width:none;white-space:nowrap',
  circlePaintStyle: {
    'circle-radius': 9,
    'circle-stroke-color': 'rgb(255, 255, 255)',
    'circle-color': 'rgb(172, 204, 251)',
    'circle-stroke-width': 2,
    'circle-opacity': 1,
    'circle-blur': 0.2,
  },
  linePaintStyle: {
    'line-color': '#1369E9',
    'line-width': 2,
  },
  hoverStyle: {
    'line-color': 'rgb(132, 163, 209)',
    'line-width': 2,
  },
  unselectedStyle: {
    'fill-color': 'rgb(255, 255, 255)',
    'fill-opacity': 0.5,
  },
};

export const ONBOARDING_STATUSES = { UNDER_REVIEW: 'under-review', APPROVED: 'approved' };
